/**
 * Template management for DOCX export customization.
 * Handles uploading, listing, downloading, and managing custom DOCX templates.
 */

export interface Template {
  id: number | string;
  name: string;
  filename: string;
  description?: string | null;
  isDefault: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface TemplateManagerConfig {
  /** Called when the templates list changes */
  onTemplatesChanged?: () => void;
  /** Get the auth token for API requests */
  getAuthToken?: () => string | null;
}

export class TemplateManager {
  private templates: Template[] = [];
  private config: TemplateManagerConfig;

  constructor(config: TemplateManagerConfig = {}) {
    this.config = config;
  }

  /**
   * Helper for authenticated API calls
   */
  private async apiFetch<T = unknown>(url: string, options: RequestInit = {}): Promise<T> {
    const token = this.config.getAuthToken?.();

    const headers: HeadersInit = {
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }

    // Only set Content-Type for JSON requests (not FormData)
    if (!(options.body instanceof FormData)) {
      (headers as Record<string, string>)['Content-Type'] = 'application/json';
    }

    const res = await fetch(url, {
      ...options,
      headers,
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || `Request failed: ${res.status}`);
    }

    return res.json();
  }

  /**
   * Load templates from backend
   */
  async loadTemplates(): Promise<void> {
    try {
      const data = await this.apiFetch<{ templates: Template[] }>('/api/templates');
      this.templates = data.templates || [];
      this.config.onTemplatesChanged?.();
    } catch (err) {
      console.error('Load templates error:', err);
      throw err;
    }
  }

  /**
   * Upload a new template
   */
  async uploadTemplate(file: File, name: string, description?: string): Promise<Template> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', name);
    if (description) {
      formData.append('description', description);
    }

    try {
      const token = this.config.getAuthToken?.();
      const headers: HeadersInit = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch('/api/templates', {
        method: 'POST',
        headers,
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Upload failed');
      }

      const data = await response.json();

      // Refresh the list
      await this.loadTemplates();

      return data.template;
    } catch (err) {
      console.error('Upload template error:', err);
      throw err;
    }
  }

  /**
   * Download a template
   */
  async downloadTemplate(templateId: number | string, filename: string): Promise<void> {
    try {
      const token = this.config.getAuthToken?.();
      const headers: HeadersInit = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`/api/templates/${templateId}/download`, { headers });
      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download template error:', err);
      throw err;
    }
  }

  /**
   * Download the default Leedoc template (programmatically generated)
   */
  async downloadDefaultTemplate(): Promise<void> {
    try {
      const response = await fetch('/api/templates/default/download');
      if (!response.ok) {
        throw new Error('Failed to generate default template. Please try again.');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'leedoc-default.docx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download default template error:', err);
      throw err;
    }
  }

  /**
   * Delete a template
   */
  async deleteTemplate(templateId: number | string): Promise<void> {
    try {
      await this.apiFetch(`/api/templates/${templateId}`, {
        method: 'DELETE',
      });

      // Refresh the list
      await this.loadTemplates();
    } catch (err) {
      console.error('Delete template error:', err);
      throw err;
    }
  }

  /**
   * Set a template as default
   */
  async setDefaultTemplate(templateId: number | string): Promise<void> {
    try {
      await this.apiFetch(`/api/templates/${templateId}/default`, {
        method: 'PUT',
      });

      // Refresh the list
      await this.loadTemplates();
    } catch (err) {
      console.error('Set default template error:', err);
      throw err;
    }
  }

  /**
   * Clear default template (use system default)
   */
  async clearDefaultTemplate(templateId: number | string): Promise<void> {
    try {
      await this.apiFetch(`/api/templates/${templateId}/default`, {
        method: 'DELETE',
      });

      // Refresh the list
      await this.loadTemplates();
    } catch (err) {
      console.error('Clear default template error:', err);
      throw err;
    }
  }

  /**
   * Get all templates
   */
  getTemplates(): Template[] {
    return this.templates;
  }

  /**
   * Get default template
   */
  getDefaultTemplate(): Template | null {
    return this.templates.find(t => t.isDefault) || null;
  }

  /**
   * Get template by ID
   */
  getTemplate(id: number | string): Template | undefined {
    return this.templates.find(t => String(t.id) === String(id));
  }
}
